import Pie2DDataset from '@fusioncharts/charts/src/dataset/pie2d';
import { PATTERN_TYPES } from '@fusioncharts/charts/src/dataset/column';
import { COMMASPACE, ZEROSTRING, pluck, pluckNumber,
  toRaphaelColor, parseUnsafeString, getDashStyle, getValidValue,
  parseTooltext, getFirstValue, convertColor, TRACKER_FILL, hashify,
  BLANKSTRING, extend2, getSuggestiveRotation } from '@fusioncharts/core/src/lib';
import { addDep, getDep } from '@fusioncharts/core/src/dependency-manager';
import multilevelpieAnimation from './index.animation';
import { priorityList } from '@fusioncharts/core/src/schedular';
import raphaelShapesRingpath from '@fusioncharts/core/src/_internal/redraphael/redraphael-shapes/redraphael-shapes.ringpath';

let Raphael = getDep('redraphael', 'plugin'),
  BLANK = '',
  NORMALSTRING = 'normal',
  COLOR_FFFFFF = 'FFFFFF',
  COLOR_000000 = '000000',
  HUNDRED_PERCENT = '100%',
  ZERO_STR = '0',
  pInt = function (s, mag) {
    return parseInt(s, mag || 10);
  },
  DASH_DEF = 'none',
  UNDEF,
  ROLLOVER = 'DataPlotRollOver',
  ROLLOUT = 'DataPlotRollOut',
  POINTER = 'pointer',
  EVENTARGS = 'eventArgs',
  PX = 'px',
  INNERRADIUS = /^[0-9]+\%?$|^([0-9]+[.][0-9]+)\%?$/,
  calculateInnerRadius = function (innerRadius, pieRadius) {
    let innerRadiusLength = innerRadius && innerRadius.length, // eslint-disable-line good-practices/no-single-usage-variable
      calculatedInnerRadius;
    if (!innerRadius) {
      return 0;
    }
    if (innerRadius.charAt(innerRadiusLength - 1) === '%') {
      // execute when innerRadius is given as percentage
      calculatedInnerRadius = pluckNumber(pieRadius) * (pluckNumber(innerRadius.split('%')[0]) / 100);
    } else {
      // execute when innerRadius is given in pixels.
      calculatedInnerRadius = pluckNumber(innerRadius);
    }

    if (calculatedInnerRadius < pieRadius) {
      return calculatedInnerRadius;
    } return 0;
  };
addDep({
  name: 'multilevelpieAnimation',
  type: 'animationRule',
  extension: multilevelpieAnimation
});

// Adding required shapes for chart.
raphaelShapesRingpath(Raphael);
class MultiLevelPieDataset extends Pie2DDataset {
  constructor () {
    super();

    this.components = {
      data: []
    };
    this.graphics = {
    };
  }
  /**
   * Sets the type of the component
   * @return {string} type
   */
  getType () {
    return 'dataset';
  }
  /**
   * Sets the name of the component
   * @return {string} name
   */
  getName () {
    return 'multiLevelPie';
  }
  configureAttributes (datasetJSON) {
    if (!datasetJSON) {
      return false;
    }
    this.config.JSONData = datasetJSON;
    var fontBdrColor,
      dataSet = this,
      chart = dataSet.getFromEnv('chart'),
      chartConfig = chart.config,
      dataSetConfig = dataSet.config || (dataSet.config = {}),
      dataLabels = dataSetConfig.dataLabelOptions || (dataSetConfig.dataLabelOptions = {}),
      piePlotOptions = dataSetConfig.piePlotOptions,
      style = chart.config.style,
      chartAttrs = chart.getFromEnv('dataSource').chart,
      centerAngle = pluckNumber(-chartAttrs.centerangle, 180),
      totalAngle = pluckNumber(chartAttrs.totalangle, 360),
      tranverseToRoot = function (dataInstance, evtType) {
        let dataObj = dataInstance;
        while (dataObj.graphics.element) {
          (dataObj.graphics.element).attr({
            fill: evtType === 'mouseover' ? dataSetConfig.hoverFillColor : (dataObj.config || this.data('plotItem')).color
          });
          dataObj = dataObj.config.parent;
        }
      },
      traverseAllChildren = function (dataObj, evtType) {
        var k;
        (dataObj.graphics.element).attr({
          fill: evtType === 'mouseover' ? dataSetConfig.hoverFillColor : (dataObj.config || this.data('plotItem')).color
        });
        for (k = 0; k < (dataObj.components.data && dataObj.components.data.length); k++) {
          traverseAllChildren.call(this, dataObj.components.data[k], evtType);
        }
      },
      mouseEvtHandler = function (evtType) {
        if (!dataSetConfig.useHoverColor) {
          return;
        }
        var point = this.data('plotItem'),
          selfRef = point.selfRef;

        if (!dataSetConfig.highlightParentPieSlices && !dataSetConfig.highlightChildPieSlices) {
          (selfRef.graphics.element).attr({
            fill: evtType === 'mouseover' ? dataSetConfig.hoverFillColor : (selfRef.config || point).color
          });
        } else if (!dataSetConfig.highlightParentPieSlices && dataSetConfig.highlightChildPieSlices) {
          traverseAllChildren.call(this, selfRef, evtType);
        } else if (dataSetConfig.highlightParentPieSlices && !dataSetConfig.highlightChildPieSlices) {
          tranverseToRoot.call(this, selfRef, evtType);
        } else if (dataSetConfig.highlightParentPieSlices && dataSetConfig.highlightChildPieSlices) {
          tranverseToRoot.call(this, selfRef, evtType);
          traverseAllChildren.call(this, selfRef, evtType);
        }
      };
    dataSetConfig.usePattern = pluckNumber(chartConfig.usePattern, 0);
    dataSetConfig.patternType = PATTERN_TYPES.includes(datasetJSON.patterntype)
      ? datasetJSON.patterntype
      : chartConfig.patternType;
    dataSetConfig.patternDensity = pluckNumber(datasetJSON.patterndensity, chartConfig.patternDensity);
    dataSetConfig.patternAlpha = pluckNumber(datasetJSON.patternalpha, chartConfig.patternAlpha);
    dataSetConfig.patternBgColor = pluck(datasetJSON.patternbgcolor, dataSetConfig.patternBgColor);
    dataSetConfig.enableAnimation = pluckNumber(chartAttrs.animation,
      chartAttrs.defaultanimation, 1);
    dataSetConfig.animation = !dataSetConfig.enableAnimation ? false : {
      duration: pluckNumber(chartAttrs.animationduration, chartAttrs.moveduration, 1) * 1000
    };
    dataSetConfig.showShadow = pluckNumber(chartAttrs.showshadow, 0);
    dataSetConfig.useHoverColor = Boolean(pluckNumber(chartAttrs.usehovercolor, 1));
    if (dataSetConfig.usePattern) {
      dataSetConfig.useHoverColor = 0;
    }
    dataSetConfig.showTextOutline = Boolean(pluckNumber(chartAttrs.textoutline, 0));
    dataSetConfig.hoverFillColor = convertColor(pluck(chartAttrs.hoverfillcolor,
      'FF5904'), pluckNumber(chartAttrs.hoverfillalpha, 100));
    fontBdrColor = getFirstValue(chartAttrs.valuebordercolor, BLANK);
    fontBdrColor = fontBdrColor ? convertColor(
      fontBdrColor, pluckNumber(chartAttrs.valueborderalpha, chartAttrs.valuebgalpha,
        chartAttrs.valuealpha, 100)) : BLANK;
    // create the style object if required.
    // fix for multilevel pie datavalue cosmetics issue RED-1594
    !dataLabels.style && (dataLabels.style = {
      fontFamily: pluck(chartAttrs.valuefont, style.fontFamily),
      fontSize: pluckNumber(this.computeFontSize(chartAttrs.valuefontsize),
        pInt(style.fontSize, 10)) + PX,
      color: convertColor(pluck(chartAttrs.valuefontcolor, style.color),
        pluckNumber(chartAttrs.valuefontalpha,
          chartAttrs.valuealpha, 100)),
      fontWeight: pluckNumber(chartAttrs.valuefontbold) ? 'bold' : NORMALSTRING,
      fontStyle: pluckNumber(chartAttrs.valuefontitalic) ? 'italic' : NORMALSTRING,
      backgroundColor: chartAttrs.valuebgcolor
        ? convertColor(chartAttrs.valuebgcolor,
          pluckNumber(chartAttrs.valuebgalpha, chartAttrs.valuealpha, 100)) : BLANK,
      border: fontBdrColor || chartAttrs.valuebgcolor
        ? (pluckNumber(chartAttrs.valueborderthickness, 1) + 'px solid') : BLANK,
      borderPadding: pluckNumber(chartAttrs.valueborderpadding, 2),
      borderThickness: pluckNumber(chartAttrs.valueborderthickness, style.borderThickness, 1),
      borderRadius: pluckNumber(chartAttrs.valueborderradius, style.borderRadius, 0),
      borderColor: fontBdrColor,
      borderDash: pluckNumber(chartAttrs.valueborderdashed, 0)
        ? getDashStyle(pluckNumber(chartAttrs.valueborderdashlen, 4),
          pluckNumber(chartAttrs.valueborderdashgap, 2)) : DASH_DEF
    });

    // stop point slicing
    !piePlotOptions && (piePlotOptions = (dataSetConfig.piePlotOptions = {}));
    piePlotOptions.allowPointSelect = false;
    dataSetConfig.borderAlpha = pluck(chartAttrs.plotborderalpha, chartAttrs.pieborderalpha, 100);
    dataSetConfig.borderColor = convertColor(pluck(chartAttrs.plotbordercolor,
      chartAttrs.piebordercolor, COLOR_FFFFFF), chartAttrs.showplotborder !== ZEROSTRING
      ? pluck(chartAttrs.plotborderalpha, chartAttrs.pieborderalpha, 100) : 0);
    dataSetConfig.rawborderColor = pluck(chartAttrs.plotbordercolor,
      chartAttrs.piebordercolor, COLOR_FFFFFF);
    dataSetConfig.showplotborder = pluckNumber(chartAttrs.showplotborder, 1);
    dataSetConfig.showTooltip = pluckNumber(chartAttrs.showtooltip, 1);

    dataSetConfig.borderWidth = pluckNumber(chartAttrs.pieborderthickness,
      chartAttrs.plotborderthickness, 1);

    piePlotOptions.startingAngle = 0; // set the chart's startingAngle as 0 [alwase]
    piePlotOptions.size = HUNDRED_PERCENT;

    dataSetConfig.highlightParentPieSlices = pluckNumber(chartAttrs.highlightparentpieslices,
      chartAttrs.highlightparentpienodes, 1);
    dataSetConfig.highlightChildPieSlices = pluckNumber(chartAttrs.highlightchildpieslices,
      chartAttrs.highlightchildpienodes, 0);

    dataSetConfig.showLabels = pluckNumber(chartAttrs.showlabels, 1);
    dataSetConfig.showValues = pluckNumber(chartAttrs.showvalues, 0);
    dataSetConfig.showValuesInTooltip = pluckNumber(chartAttrs.showvaluesintooltip,
      chartAttrs.showvalues, 0);
    dataSetConfig.showPercentValues = pluckNumber(chartAttrs.showpercentvalues,
      chartAttrs.showpercentagevalues, 0);
    dataSetConfig.showPercentInTooltip = pluckNumber(chartAttrs.showpercentintooltip, 0);
    dataSetConfig.toolTipSepChar = pluck(chartAttrs.tooltipsepchar, chartAttrs.hovercapsepchar, COMMASPACE);
    dataSetConfig.labelSepChar = parseUnsafeString(pluck(chartAttrs.labelsepchar, dataSetConfig.toolTipSepChar));
    dataSetConfig.tooltext = chartAttrs.plottooltext;
    dataSetConfig.alpha = pluck(chartAttrs.plotfillalpha, chartAttrs.piefillalpha, 100);
    dataSetConfig.startAngle = (centerAngle - (totalAngle / 2)) * (Math.PI / 180);
    dataSetConfig.endtAngle = (centerAngle + (totalAngle / 2)) * (Math.PI / 180);
    dataSetConfig.initialAngle = dataSetConfig.endtAngle;
    dataSetConfig.originX = pluckNumber(chartAttrs.originx);
    dataSetConfig.originY = pluckNumber(chartAttrs.originy);

    dataSetConfig.events = {
      mouseOver: function () {
        mouseEvtHandler.call(this, 'mouseover');
      },
      mouseOut: function () {
        mouseEvtHandler.call(this, 'mouseout');
      }
    };

    // remove the plotboder
    chartConfig.plotBorderWidth = 0;

    // get the label rotation, overlap and ellipses attributes if present
    dataSetConfig.autoRotateLabels = (chartConfig.autorotatelabels) ? pluckNumber(chartConfig.autorotatelabels) : 0;
    dataSetConfig.useEllipsesWhenOverflow = (chartConfig.useellipseswhenoverflow) ? pluckNumber(chartConfig.useellipseswhenoverflow) : 1;
    dataSetConfig.skipOverlapLabels = (chartConfig.skipoverlaplabels) ? pluckNumber(chartConfig.skipoverlaplabels) : 0;

    // remove the plotboder
    chartConfig.plotBorderWidth = 0;
    dataSetConfig.maxLevel = dataSet.addMSPieCat(dataSet.config.JSONData, 1, dataSet, dataSetConfig.startAngle,
      dataSetConfig.endtAngle);
    dataSetConfig.pieRadius = parseInt(chartAttrs.pieradius, 10);

    // dataSetConfig.innerRadius = pluckNumber(Number(pluck(chartAttrs.innerradius, '0%').split('%')[0]), 0);
    dataSetConfig.innerRadius = ZERO_STR;
    (INNERRADIUS.test(chartAttrs.innerradius)) && (dataSetConfig.innerRadius = pluck(chartAttrs.innerradius));

    dataLabels.distance = 0;
    dataLabels.placeLabelsInside = true;
  }
  // doHide is a Boolean Flag to hide the element or not.
  // All the graphical elements in excess are pushed to the graphics pool. They are hidden only after the
  // entire draw is complete.
  removalFn (ele, prop) {
    var dataSet = this,
      // eslint-disable-next-line good-practices/no-function-dependency
      hideFN = function () {
        this.remove();
      };

    dataSet.getFromEnv('animationManager').setAnimation({
      el: ele,
      label: prop === 'element' ? 'ringpath' : 'label',
      component: dataSet,
      callback: hideFN
    });
  }
  removeGraphics (obj) {
    var i,
      prop,
      dataSet = this,
      childData = obj.components && obj.components.data,
      len,
      graphics = obj.graphics;
    if (childData) {
      len = childData.length;
      for (i = 0; i < len; i += 1) {
        dataSet.removeGraphics(childData[i]);
      }
    }

    if (obj.graphics) {
      for (prop in graphics) {
        if (graphics.hasOwnProperty(prop)) {
          dataSet.removalFn(obj.graphics[prop], prop);
        }
      }
    }
  }
  removeChild (removalStore, doHide, type) {
    var i,
      elemObj,
      dataSet = this;
    if (removalStore.length) {
      for (i = 0; i < removalStore.length; i += 1) {
        elemObj = removalStore[i];
        if (type) {
          dataSet.removalFn(elemObj, doHide, type);
        } else {
          dataSet.removeGraphics(elemObj, doHide);
        }
      }
    } else {
      // if the removalStore is in the Object structure.
      for (i in removalStore) {
        dataSet.removeChild(removalStore[i], doHide, i);
      }
    }
  }
  addMSPieCat (catArr, level, parentObj, startAngle, endAngle) {
    var dataObj,
      dataObjLen,
      cat = extend2([], catArr),
      catObjLen,
      catLen = cat.length,
      dataSet = this,
      data = parentObj.components.data,
      dataSetConfig = dataSet.config,
      numberFormatter = dataSet.getFromEnv('number-formatter'),
      sharePercent,
      totalValue = 0,
      catObj,
      catVal,
      i,
      label,
      labelSepChar = dataSetConfig.labelSepChar,
      fillalpha,
      valueStr,
      pValueStr,
      toolText,
      displayValue,
      rawColor,
      showLabel,
      patternType,
      patternAngle,
      patternDensity,
      patternSize,
      patternAlpha,
      patternBgColor,
      showPlotBorder,
      maxLevel = level,
      dataLength = data.length, // eslint-disable-line good-practices/no-single-usage-variable
      removalFn = function () {
        dataSet.removeChild.apply(dataSet, arguments);
      },
      catLength = cat.length,
      totAngle = endAngle - startAngle, // eslint-disable-line good-practices/no-single-usage-variable
      tempAngle,
      cumilative = 0, // eslint-disable-line good-practices/no-single-usage-variable
      preDataOldEndAngle;

    for (i = 0; i < catLen; i += 1) {
      // store for letter use
      catObj = cat[i];
      catObj._userValue = numberFormatter.getCleanValue(catObj.value, true);
      catObj._value = pluckNumber(catObj._userValue, 1);
      totalValue += catObj._value;
    }
    // Total value can't be zero, since its used in denominator to find ratio.
    totalValue = totalValue || 1;
    // add the category
    sharePercent = totAngle / totalValue; // eslint-disable-line good-practices/no-single-usage-variable
    for (i = catLen - 1; i >= 0; i -= 1) {
      catObj = cat[i];
      catVal = sharePercent * catObj._value;
      label = parseUnsafeString(pluck(catObj.label, catObj.name));
      valueStr = catObj._userValue !== null
        ? numberFormatter.dataLabels(catObj._userValue) : BLANK;
      pValueStr = numberFormatter.percentValue((catObj._value /
                    totalValue) * 100);
      // pointIndex = sLevel.length - 1;
      fillalpha = pluckNumber(catObj.alpha, dataSetConfig.alpha);
      showLabel = pluckNumber(catObj.showlabel, dataSetConfig.showLabels);
      showPlotBorder = pluckNumber(dataSetConfig.showplotborder, 1);
      displayValue = (showLabel === 1) ? label : BLANK;

      if (pluckNumber(catObj.showvalue, dataSetConfig.showValues) === 1) {
        if (dataSetConfig.showPercentValues) {
          displayValue += displayValue !== BLANK ? (labelSepChar + pValueStr) : pValueStr;
        } else if (valueStr !== UNDEF && valueStr !== BLANK) {
          displayValue += displayValue !== BLANK ? (labelSepChar + valueStr) : valueStr;
        }
      }
      toolText = dataSetConfig.showTooltip ? parseUnsafeString(pluck(catObj.tooltext, catObj.hovertext,
        dataSetConfig.tooltext), false) : UNDEF;
      if (toolText === BLANK) {
        toolText = label;
        if (dataSetConfig.showValuesInTooltip) {
          if (dataSetConfig.showPercentInTooltip) {
            toolText += toolText !== BLANK ? (labelSepChar + pValueStr) : pValueStr;
          } else if (valueStr !== UNDEF && valueStr !== BLANK) {
            toolText += toolText !== BLANK ? (labelSepChar + valueStr) : valueStr;
          }
        }
      } else {
        toolText = parseTooltext(toolText, [1, 2, 3, 14], {
          percentValue: pValueStr,
          label: label,
          formattedValue: valueStr
        }, catObj);
      }

      dataObj = data[i];
      tempAngle = startAngle + cumilative;
      cumilative += catVal;
      if (!dataObj) {
        dataObj = data[i] = {
          components: {
            data: []
          },
          config: {},
          graphics: {}
        };
      }
      // store old end angle for newly added element's aimation position
      if (dataObj.graphics.element) {
        preDataOldEndAngle = dataObj.config.startAngle + dataObj.config.angleStrech;
      }
      patternType = PATTERN_TYPES.includes(catObj.patterntype)
        ? catObj.patterntype
        : dataSetConfig.patternType;
      patternAngle = pluckNumber(catObj.patternangle,
        dataSet.getFromEnv('chart-attrib').patternangle, patternType === PATTERN_TYPES[0] ? 40 : 0);
      patternDensity = pluckNumber(catObj.patterndensity, dataSetConfig.patternDensity);
      patternSize = pluckNumber(catObj.patternsize,
        patternType === PATTERN_TYPES[0] ? 2 : 4);
      patternAlpha = pluckNumber(catObj.patternalpha, dataSetConfig.patternAlpha);
      patternBgColor = pluck(catObj.patternbgcolor, dataSetConfig.patternBgColor);
      rawColor = catObj.color || dataSet.getFromEnv('color-manager').getPlotColor();
      dataObj.config = {
        patternType,
        patternAngle,
        patternDensity,
        patternSize,
        patternAlpha,
        patternBgColor,
        initialAngle: preDataOldEndAngle || (parentObj.config || parentObj.config).initialAngle,
        startAngle: tempAngle,
        alpha: fillalpha,
        angleStrech: catVal,
        level: level,
        displayValue: displayValue,
        showLabel: showLabel,
        showPlotBorder: showPlotBorder,
        toolText: toolText,
        link: getValidValue(catObj.link),
        rawColor,
        doNotSlice: true, // added to stop slicing
        color: convertColor(rawColor, fillalpha),
        borderAlpha: pluck(catObj.plotborderalpha, dataSetConfig.borderAlpha),
        borderWidth: pluckNumber(catObj.borderwidth, dataSetConfig.borderWidth),
        borderColor: pluck(catObj.bordercolor, dataSetConfig.borderColor),
        rawborderColor: pluck(catObj.bordercolor, dataSetConfig.rawborderColor),
        dashStyle: pluckNumber(catObj.valueborderdashed, 0)
          ? getDashStyle(pluckNumber(catObj.borderdashlen, 4),
            pluckNumber(catObj.borderdashgap, 2)) : DASH_DEF,
        shadow: {
          opacity: Math.round(fillalpha > 50 ? fillalpha * fillalpha * fillalpha * 0.0001
            : fillalpha * fillalpha * 0.01) * 0.01
        },
        isSingleTon: !((catLen > 1))
      };
      if (dataSetConfig.usePattern) {
        dataObj.config.borderColor = dataObj.config.color;
        dataObj.config.rawborderColor = hashify(rawColor);
      }
      dataObj.config.parent = parentObj;

      dataObj.config.dataLabelStyle = dataSet._configureDataLabelStyle(catObj);

      if (catObj.category) {
        maxLevel = Math.max(maxLevel, dataSet.addMSPieCat(catObj.category, level + 1,
          dataObj, tempAngle, (catVal + tempAngle)));
        if ((dataObjLen = dataObj.components.data.length) > (catObjLen = catObj.category.length)) {
          removalFn(dataObj.components.data.splice(dataObjLen - 1, catObjLen));
        }
      } else {
        dataObjLen = dataObj.components.data.length;
        if (dataObjLen) {
          // no child category, recursively remove existing childs (dataObj.components.data)
          removalFn(dataObj.components.data.splice(0, dataObjLen));
        }
      }
    }
    // remove estra data
    if (dataLength > catLength) {
      // recursively remove elements with child
      removalFn(data.splice(catLength));
    }
    return maxLevel;
  }

  parsePlotAttributes (parentInstance) {
    var angle,
      parentObj = parentInstance,
      _textAttrs,
      centerDistance,
      level,
      dataSet = this,
      dataSetConfig = dataSet.config || {},
      chart = dataSet.getFromEnv('chart'),
      chartConfig = chart.config,
      len = dataSet.components.data.length, // eslint-disable-line good-practices/no-single-usage-variable
      seriesDataLabelsStyle = chartConfig.dataLabelStyle,
      setDataLabelStyle,
      canvasWidth = chartConfig.canvasWidth,
      canvasHeight = chartConfig.canvasHeight,
      cx = pluckNumber(dataSetConfig.originX, chartConfig.canvasLeft + canvasWidth * 0.5),
      cy = pluckNumber(dataSetConfig.originY, chartConfig.canvasTop + canvasHeight * 0.5),
      r,
      r2,
      color,
      val,
      displayValue,
      setLink,
      angle1,
      angle2,
      i,
      setGraphics,
      setConfig,
      element,
      pieSize = pluckNumber(dataSetConfig.pieRadius * 2, Math.min(canvasWidth, canvasHeight)),
      // innerRadius in diameter of inner circle
      innerRadius = calculateInnerRadius(dataSetConfig.innerRadius, pieSize / 2),
      pool = dataSet.config.pool || (dataSet.config.pool = {}),
      // either the user-specified size is chosen or the minimum aspect dimension is choosen
      // pieSize is diameter of full pie
      seriesHalfWidth = (pieSize - 2 * innerRadius) / (2 * dataSetConfig.maxLevel);
    dataSetConfig.innerSize = innerRadius * 2;
    if (!parentObj) {
      parentObj = dataSet;
    }
    len = parentObj.components.data.length; // eslint-disable-line good-practices/no-single-usage-variable
    for (i = 0; i < len; i += 1) {
      dataSet.parsePlotAttributes(parentObj.components.data[i]);
    }
    setConfig = parentObj.config;
    level = setConfig.level;
    if (level) {
      r = ((level) * seriesHalfWidth) + innerRadius;
      r2 = ((level - 1) * seriesHalfWidth) + innerRadius;

      // Save the radius value for later thickness calculation
      setConfig.outerRadius = r;
      setConfig.innerRadius = r2;

      setGraphics = parentObj.graphics;
      val = setConfig.angleStrech;
      displayValue = setConfig.displayValue;
      // toolText = setConfig.toolText;
      setLink = !!setConfig.link;
      color = setConfig.color;

      angle1 = setConfig.startAngle;
      angle2 = angle1 + setConfig.angleStrech;
      element = setGraphics.element;
      if (pool.element && pool.element.length) {
        element = setGraphics.element = element || pool.element.shift();
      }
      setConfig.plotItem = {
        chart: chart,
        link: setConfig.link,
        value: val,
        color: color,
        labelText: displayValue,
        graphics: {
          element: element
        },
        selfRef: parentObj
      };
      setConfig.eventArgs = {
        alpha: setConfig.alpha,
        borderAlpha: setConfig.borderAlpha,
        link: setConfig.link,
        label: setConfig.displayValue,
        showLabel: setConfig.showLabel,
        toolText: setConfig.toolText,
        color: setConfig.rawColor,
        borderColor: setConfig.rawborderColor,
        borderThickness: setConfig.borderWidth,
        showPlotBorder: setConfig.showPlotBorder
      };
      if (dataSetConfig.usePattern) {
        setConfig.eventArgs.pattern = {
          patternType: setConfig.patternType,
          patternColor: setConfig.rawColor,
          patternBgColor: setConfig.patternBgColor === TRACKER_FILL ? UNDEF : setConfig.patternBgColor
        };
        delete setConfig.eventArgs.color;
      }
      setConfig.props = {
        element: {
          attr: {
            ringpath: [cx, cy, r, r2, angle1, angle2],
            'stroke-width': setConfig.borderWidth,
            'stroke': setConfig.borderColor,
            fill: toRaphaelColor(setConfig.color),
            'stroke-dasharray': setConfig.dashStyle,
            cursor: setLink ? POINTER : BLANKSTRING
          }
        }
      };

      setConfig.props.element.patternAttr = {
        patternType: setConfig.patternType,
        patternAngle: setConfig.patternAngle,
        patternDensity: setConfig.patternDensity,
        patternSize: setConfig.patternSize,
        color: setConfig.color,
        patternAlpha: setConfig.patternAlpha,
        patternBgColor: setConfig.patternBgColor,
        alpha: setConfig.alpha,
        x: cx,
        y: cy
      };
      if ((displayValue !== UNDEF) && (displayValue !== BLANKSTRING)) {
        angle = (angle1 + angle2) / 2;
        // for the innermost Concentric circle the center distance is presumed to be zero
        centerDistance = ((r2 === 0) && setConfig.isSingleTon) ? 0 : r2 + ((r - r2) / 2);
        if (!(_textAttrs = setConfig._textAttrs)) {
          _textAttrs = setConfig._textAttrs = {};
        }

        setDataLabelStyle = setConfig.dataLabelStyle;

        _textAttrs.text = displayValue;
        _textAttrs.fill = setDataLabelStyle.color || COLOR_000000;
        _textAttrs.direction = chartConfig.textDirection;
        _textAttrs.cursor = setLink ? POINTER : BLANKSTRING;
        _textAttrs.x = cx + (centerDistance * Math.cos(angle));
        _textAttrs.y = cy + (centerDistance * Math.sin(angle));
        _textAttrs['line-height'] = seriesDataLabelsStyle.lineHeight;
        _textAttrs['text-bound'] = [setDataLabelStyle.backgroundColor,
          setDataLabelStyle.borderColor,
          seriesDataLabelsStyle.borderThickness,
          seriesDataLabelsStyle.borderPadding,
          seriesDataLabelsStyle.borderRadius,
          seriesDataLabelsStyle.borderDash];
      }
    }
  }

  /**
   * Parses label attributes and position during post space management
   *
   * @param {any} parentObj
   * @memberof MultiLevelPieDataset
   */
  parseLabelAttributes (parentInstance) {
    var dataSet = this,
      parentObj = parentInstance,
      len = dataSet.components.data.length, // eslint-disable-line good-practices/no-single-usage-variable
      displayValue,
      i,
      setConfig;

    // dataLabelsLayer.show();
    !parentObj && (parentObj = dataSet);
    len = parentObj.components.data.length; // eslint-disable-line good-practices/no-single-usage-variable
    for (i = 0; i < len; i += 1) {
      dataSet.parseLabelAttributes(parentObj.components.data[i]);
    }
    setConfig = parentObj.config;
    // plotItem = setConfig.plotItem;
    displayValue = setConfig.displayValue;
    if (setConfig.level) {
      if ((displayValue !== UNDEF) && (displayValue !== BLANKSTRING)) {
        setConfig.props.label = {
          attr: setConfig._textAttrs
        };
      }
    }
  }

  draw (parentInstance) {
    var dataSet = this,
      parentObj = parentInstance,
      dataSetConfig = dataSet.config,
      chart = dataSet.getFromEnv('chart'),
      dataSetComponents = dataSet.components,
      len = dataSetComponents.data.length, // eslint-disable-line good-practices/no-single-usage-variable
      i,
      setGraphics,
      setConfig,
      element,
      dataLabelsLayer = chart.getChildContainer('datalabelsGroup'),
      events = dataSetConfig.events || {},
      plotHoverFN = function (e) {
        var o = this,
          mouseOver = events.mouseOver,
          plotItem = o.plotItem || o.data('plotItem'),
          dataset = plotItem.chart.getDatasets()[0]; // eslint-disable-line good-practices/no-single-usage-variable
        chart.plotEventHandler(o, e, ROLLOVER);
        mouseOver && mouseOver.call(o);
        dataset.fireEvent('datasetrollover', { pValue: plotItem.pValue, value: plotItem.value, displayValue: setConfig.displayValue });
      },
      plotMouseOut = function (e) {
        var o = this,
          mouseOut = events.mouseOut,
          plotItem = o.plotItem || o.data('plotItem'),
          chartAPI = plotItem.chart,
          dataset = chartAPI.getDatasets()[0]; // eslint-disable-line good-practices/no-single-usage-variable
        chartAPI.plotEventHandler(o, e, ROLLOUT);
        mouseOut && mouseOut.call(o);
        dataset.fireEvent('datasetrollout', { pValue: plotItem.pValue, value: plotItem.value, displayValue: setConfig.displayValue });
      },
      // Fired when clicked over the hot elements.
      clickFunc = function (setDataArr) {
        var ele = this; // eslint-disable-line good-practices/no-single-usage-variable
        chart.plotEventHandler(ele, setDataArr);
      },
      pool = dataSet.config.pool || (dataSet.config.pool = {}),
      removeDataArr = dataSetComponents.removeDataArr;
    // dataSetConfig.innerSize = innerRadius * 2;

    removeDataArr && removeDataArr.length && dataSet.removeElement();
    if (!parentObj) {
      parentObj = dataSet;
      dataLabelsLayer.css(chart.config.dataLabelStyle);
    }
    len = parentObj.components.data.length; // eslint-disable-line good-practices/no-single-usage-variable
    for (i = 0; i < len; i += 1) {
      dataSet.draw(parentObj.components.data[i]);
    }
    setConfig = parentObj.config;
    if (setConfig.level) {
      setGraphics = parentObj.graphics;
      element = setGraphics.element;
      if (pool.element && pool.element.length) {
        element = setGraphics.element = element || pool.element.shift();
      }
      // Create/reuse element
      element = setGraphics.element = dataSet.getFromEnv('animationManager').setAnimation({
        el: setGraphics.element || 'ringpath',
        attr: setConfig.props.element.attr,
        container: chart.getChildContainer('plotGroup'),
        component: dataSet,
        state: 'appearing',
        label: 'ringpath'
      });

      element.addPattern(setConfig.props.element.patternAttr, dataSetConfig.usePattern);

      element.shadow(dataSetConfig.showShadow && !!setConfig.shadow)
        .data('plotItem', setConfig.plotItem)
        .data(EVENTARGS, setConfig.eventArgs)
        .on('fc-mouseover', plotHoverFN)
        .on('fc-mouseout', plotMouseOut)
        .on('fc-click', clickFunc);
      dataSet.getFromEnv('toolTipController').enableToolTip(element, setConfig.toolText);

      if (!i && !dataSetConfig._drawn) {
        dataSetConfig._drawn = true;
        dataLabelsLayer.show();
        dataSet.addJob('labelJob', dataSet.drawLabel.bind(dataSet), priorityList.label);
      }
    } else {
      // If the chart is already drawn, then instead of sheduling the label drawing, directly draw the
      // labels.
      dataSetConfig._drawn && dataSet.drawLabel();
      // at the end of all the tree structure, hide all the pool elements.
      dataSet.removeChild(dataSet.config.pool, true);
    }
  }
  drawLabel (parentInstance) {
    var dataSet = this,
      config = dataSet.config,
      parentObj = parentInstance,
      chart = dataSet.getFromEnv('chart'),
      smartLabel = dataSet.getFromEnv('smartLabel'),
      labelSmartObj,
      angle,
      angleStretch,
      animationManager = dataSet.getFromEnv('animationManager'),
      chartConfig = chart.config,
      labelAttrs,
      len = dataSet.components.data.length, // eslint-disable-line good-practices/no-single-usage-variable
      tooltipOptions = chartConfig.tooltip || {},
      isTooltip = tooltipOptions && tooltipOptions.enabled !== false, // eslint-disable-line good-practices/no-single-usage-variable
      toolText,
      displayValue,
      tempLabel,
      i,
      setGraphics,
      setConfig,
      label,
      events = (config || {}).events || {},
      labelHoverFN = function (e) {
        var o = this,
          mouseOver = events.mouseOver;
        chart.plotEventHandler(o, e, ROLLOVER);
        mouseOver && mouseOver.call(o);
      },
      labelOutFN = function (e) {
        var o = this,
          mouseOut = events.mouseOut;
        chart.plotEventHandler(o, e, ROLLOUT);
        mouseOut && mouseOut.call(o);
      },
      // Fired when clicked over the hot elements.
      clickFunc = function (setDataArr) {
        var ele = this; // eslint-disable-line good-practices/no-single-usage-variable
        chart.plotEventHandler(ele, setDataArr);
      },
      pool = config.pool || (config.pool = {}),
      dataLabelsLayer = chart.getChildContainer('datalabelsGroup');

    dataLabelsLayer.show();
    !parentObj && (parentObj = dataSet);
    len = parentObj.components.data.length; // eslint-disable-line good-practices/no-single-usage-variable
    for (i = 0; i < len; i += 1) {
      dataSet.drawLabel(parentObj.components.data[i]);
    }
    setConfig = parentObj.config;
    displayValue = setConfig.displayValue;
    if (setConfig.level) {
      setGraphics = parentObj.graphics;
      tempLabel = setGraphics.label;
      angleStretch = setConfig.angleStrech;
      // Condition to check if skipOverlapLabels is set to true
      if ((displayValue !== UNDEF) && (displayValue !== BLANKSTRING) && (!config.skipOverlapLabels || (config.skipOverlapLabels && angleStretch > 0.1))) {
        label = setGraphics.label;
        if (pool.label && pool.label.length) {
          label = setGraphics.label = label || pool.label.shift();
        }
        labelAttrs = setConfig.props.label.attr;

        if (config.autoRotateLabels) {
          // Angle calculation for label rotation
          angle = ((setConfig.startAngle + angleStretch / 2) * 180 / Math.PI);
          if (Math.abs(angle) > 90 && Math.abs(angle) < 180) {
            angle = -(180 - angle);
          }
          if (Math.floor(angleStretch * 180 / Math.PI) === 360) {
            angle = 0;
          }
          // Use smart label when autoRotateLabels is set to true
          smartLabel.useEllipsesOnOverflow(config.useEllipsesWhenOverflow);
          smartLabel.setStyle(chartConfig.dataLabelStyle);
          labelSmartObj = smartLabel.getSmartText(displayValue,
            (setConfig.outerRadius - setConfig.innerRadius - 10), smartLabel._lineHeight);
          labelAttrs.text = labelSmartObj.text;
          labelAttrs.transform = getSuggestiveRotation(
            parseFloat(angle),
            labelAttrs.x, labelAttrs.y);
        } else {
          labelAttrs.transform = getSuggestiveRotation(
            0,
            labelAttrs.x, labelAttrs.y);
        }
        // Create/Reuse label
        label = setGraphics.label = animationManager.setAnimation({
          el: tempLabel || 'text',
          attr: labelAttrs,
          container: dataLabelsLayer,
          component: dataSet,
          state: 'appearing',
          label: 'text'
        });
        label.outlineText(config.showTextOutline, labelAttrs.fill);
        if (!tempLabel) {
          label.on('fc-click', clickFunc)
            .on('fc-mouseout', labelOutFN)
            .on('fc-mouseover', labelHoverFN);
        }
        label.data('plotItem', setConfig.plotItem)
          .data(EVENTARGS, setConfig.eventArgs);

        isTooltip && dataSet.getFromEnv('toolTipController').enableToolTip(label, toolText);
      } else {
        // In case of extra labels, hide them
        tempLabel && animationManager.setAnimation({
          el: tempLabel,
          component: dataSet,
          label: 'text'
        });
        delete setGraphics.label;
      }
    } else {
      // at the end of all the tree structure, hide all the pool elements.
      dataSet.removeChild(dataSet.config.pool, true);
    }
  }
}
export default MultiLevelPieDataset;
